import { z } from 'zod';

import {
  CategorySchemaV3,
  DefaultPriceTierSchemaV3,
  FirstAvailableDateSchemaV3,
  HostedBySchemaV3,
  MediasSchemaV3,
  ResourceGroupSchemaV3,
  TaxSchemaV3,
  VibesSchemaV3,
} from './common';
import {
  BookingAvailabilityModeValue,
  buildQueryString,
  ExperienceImageSizeMediaSchema,
  IntegrationMetaSchema,
  ListingKind,
  ListingStatus,
  PaymentMethods,
  ProductLine,
  ResourceGroupCollectionEventType,
  ShouldValidateComplimentaryBookingSchema,
} from '../../..';
import { ExperienceVersion } from '../../../experience/const';
import { makeEndpoint } from '../../common';
import { ExperienceType } from '../../experience/experiences';
import { PromotedListingStatus } from '../../promoter/types';
import { ScheduleType } from '../../resource/schedule-time-frame';

export const GetListingV3QuerySchema = z.object({
  hydrateResourceGroups: z.boolean().default(false),
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.LATEST),
});

export const GetListingV3LocationSchema = z.object({
  name: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
});

export const GetListingV3ResponseSchema = z.object({
  bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue),
  category: CategorySchemaV3.optional().nullable(),
  defaultPriceTiers: z.array(DefaultPriceTierSchemaV3),
  description: z.string().optional().nullable(),
  eventType: z.nativeEnum(ResourceGroupCollectionEventType).optional().nullable(),
  scheduleType: z.nativeEnum(ScheduleType).optional().nullable(),
  experienceId: z.string().uuid().optional().nullable(),
  firstAvailableDate: FirstAvailableDateSchemaV3.optional().nullable(),
  id: z.string().uuid(),
  integrationMeta: z.array(IntegrationMetaSchema).nullable().optional(),
  isBookable: z.boolean(),
  isUnlisted: z.boolean(),
  headline: z.string().optional().nullable(),
  hostedBy: HostedBySchemaV3.optional().nullable(),
  kind: z.nativeEnum(ListingKind),
  location: GetListingV3LocationSchema.optional().nullable(),
  map: ExperienceImageSizeMediaSchema.nullable(),
  maxParticipants: z.number().optional().nullable(),
  medias: z.array(MediasSchemaV3),
  paymentMethods: z.array(z.nativeEnum(PaymentMethods)).optional().nullable(),
  productLine: z.nativeEnum(ProductLine),
  providerBrandId: z.string().uuid().optional().nullable(),
  providerListingId: z.string().uuid().optional().nullable(),
  providerListingStatus: z.nativeEnum(PromotedListingStatus).optional().nullable(),
  resourceGroupCollectionId: z.string().uuid().optional().nullable(),
  resourceGroups: z.array(ResourceGroupSchemaV3),
  shouldValidateComplimentaryBooking: ShouldValidateComplimentaryBookingSchema.optional().nullable(),
  status: z.nativeEnum(ListingStatus).optional().nullable(),
  taxes: z.array(TaxSchemaV3).optional().nullable(),
  title: z.string().optional().nullable(),
  type: z.nativeEnum(ExperienceType).optional().nullable(),
  vibes: z.array(VibesSchemaV3),
});

export type GetListingV3Response = z.infer<typeof GetListingV3ResponseSchema>;

export const GetListingV3Endpoint = makeEndpoint({
  method: 'GET',
  url: ({
    listingId,
    query,
    brandId,
  }: {
    listingId: string;
    query: z.input<typeof GetListingV3QuerySchema>;
    brandId: string;
  }) => `v3/brands/${brandId}/dashboard/listings/${listingId}?${buildQueryString(query)}`,
  responseSchema: GetListingV3ResponseSchema,
  querySchema: GetListingV3QuerySchema,
  requestSchema: z.void(),
});

export const GetListingSummaryV3QuerySchema = z.object({
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.LATEST),
});
export const GetListingSummaryV3ResponseSchema = z.object({
  eventType: z.nativeEnum(ResourceGroupCollectionEventType).optional().nullable(),
  scheduleType: z.nativeEnum(ScheduleType).optional().nullable(),
  experienceId: z.string().uuid().optional().nullable(),
  id: z.string().uuid(),
  isBookable: z.boolean(),
  isUnlisted: z.boolean(),
  kind: z.nativeEnum(ListingKind),
  productLine: z.nativeEnum(ProductLine),
  providerBrandId: z.string().uuid().optional().nullable(),
  resourceGroupCollectionId: z.string().uuid().optional().nullable(),
  status: z.nativeEnum(ListingStatus).optional().nullable(),
  title: z.string().optional().nullable(),
});

export const GetListingSummaryV3Endpoint = makeEndpoint({
  method: 'GET',
  url: ({
    listingId,
    query,
    brandId,
  }: {
    listingId: string;
    query: z.input<typeof GetListingV3QuerySchema>;
    brandId: string;
  }) => `v3/brands/${brandId}/dashboard/listings/${listingId}/summary?${buildQueryString(query)}`,
  responseSchema: GetListingSummaryV3ResponseSchema,
  querySchema: GetListingSummaryV3QuerySchema,
  requestSchema: z.void(),
});
